import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Battery, Check, Save } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const DashboardEnergy = () => {
  const [energyLevel, setEnergyLevel] = useState([75]);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [recentLogs, setRecentLogs] = useState<any[]>([]);

  useEffect(() => {
    loadRecentLogs();
  }, []);

  const loadRecentLogs = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from("energy_logs")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(10);

    if (error) {
      console.error("Error loading energy logs:", error);
    } else {
      setRecentLogs(data || []);
    }
  };

  const handleSaveEnergy = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast.error("Please sign in to save energy levels");
      return;
    }

    setLoading(true);
    const { error } = await supabase
      .from("energy_logs")
      .insert({
        user_id: user.id,
        energy_level: energyLevel[0],
        notes: notes || null,
      });

    if (error) {
      toast.error("Failed to save energy level");
      console.error(error);
    } else {
      toast.success("Energy level saved!");
      setNotes("");
      loadRecentLogs();
    }
    setLoading(false);
  };

  const getEnergyColor = (level: number) => {
    if (level >= 70) return "text-green-500";
    if (level >= 40) return "text-yellow-500";
    return "text-orange-500";
  };

  const getEnergyLabel = (level: number) => {
    if (level >= 70) return "High Energy";
    if (level >= 40) return "Moderate Energy";
    return "Low Energy";
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      <div className="container mx-auto px-4 py-12 max-w-6xl">
        <Link to="/dashboard">
          <Button variant="ghost" className="mb-8">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </Link>

        <section className="py-16 px-4 bg-gradient-to-br from-green-500/5 to-yellow-500/10 rounded-3xl">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className={`inline-flex p-4 rounded-2xl bg-green-500/10 mb-6 ${getEnergyColor(energyLevel[0])}`}>
                <Battery className="w-8 h-8" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">Track Your Energy</h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Monitor your energy levels and optimize your productivity throughout the day
              </p>
            </div>
            
            <Card className="border-none shadow-xl">
              <CardHeader>
                <CardTitle className="text-2xl">Energy Monitor</CardTitle>
                <CardDescription className="text-base">
                  Adjust and track your current energy state
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-8">
                <div className="text-center py-8 px-4 rounded-2xl bg-gradient-to-br from-muted/50 to-muted/20">
                  <div className={`text-7xl font-bold mb-4 ${getEnergyColor(energyLevel[0])}`}>
                    {energyLevel[0]}%
                  </div>
                  <p className={`text-2xl font-semibold ${getEnergyColor(energyLevel[0])}`}>
                    {getEnergyLabel(energyLevel[0])}
                  </p>
                </div>
                
                <div className="space-y-4">
                  <label className="text-lg font-semibold">How are you feeling right now?</label>
                  <Slider
                    value={energyLevel}
                    onValueChange={setEnergyLevel}
                    max={100}
                    step={5}
                    className="w-full"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Notes (Optional)</Label>
                  <Textarea
                    id="notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="What's affecting your energy? Any context to remember..."
                    rows={3}
                  />
                </div>

                <Button 
                  onClick={handleSaveEnergy} 
                  disabled={loading}
                  className="w-full"
                  size="lg"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {loading ? "Saving..." : "Save Energy Level"}
                </Button>
                
                <div className="p-6 rounded-xl bg-muted/50 space-y-3">
                  <p className="text-lg font-semibold mb-4">💡 Energy Optimization Tips</p>
                  <ul className="space-y-3 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <span>Take regular breaks every 45-60 minutes to maintain peak performance</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <span>Stay hydrated throughout the day for better focus and energy</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <span>Schedule difficult tasks during your high-energy periods</span>
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {recentLogs.length > 0 && (
              <Card className="border-none shadow-xl mt-8">
                <CardHeader>
                  <CardTitle className="text-2xl">Recent Energy Logs</CardTitle>
                  <CardDescription>Your energy tracking history</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {recentLogs.map((log) => (
                      <div 
                        key={log.id}
                        className="p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <Battery className={`w-5 h-5 ${getEnergyColor(log.energy_level)}`} />
                            <span className={`text-2xl font-bold ${getEnergyColor(log.energy_level)}`}>
                              {log.energy_level}%
                            </span>
                            <span className={`text-sm font-medium ${getEnergyColor(log.energy_level)}`}>
                              {getEnergyLabel(log.energy_level)}
                            </span>
                          </div>
                          <span className="text-sm text-muted-foreground">
                            {new Date(log.created_at).toLocaleString()}
                          </span>
                        </div>
                        {log.notes && (
                          <p className="text-sm text-muted-foreground mt-2 pl-8">
                            {log.notes}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default DashboardEnergy;
